import {
  InvoiceCategoryFilter,
  RateType,
  SettlementState,
} from '@treadinc/horizon-api-spec';
import { xor } from 'lodash-es';
import { computed, makeObservable, observable, runInAction } from 'mobx';

import { OrderOption } from '~components/OrderBySelector/OrderBySelector';
import { AddOnCharge } from '~hooks/useAddOns/models';

export enum SettlementSort {
  CREATED_AT = 'created_at',
  FOR = 'for',
}

export enum SettlementSearchParamName {
  SETTLEMENT_STATES = 'filter[states]',
  SETTLEMENT_CATEGORY = 'filter[category]',
  END_DATE = 'filter[end_date]',
  START_DATE = 'filter[start_date]',
  PROJECTS = 'filter[project_ids]',
  CUSTOMERS = 'filter[customer_account_ids]',
  VENDORS = 'filter[vendor_account_ids]',
  TRUCKS = 'filter[truck_ids]',
  DRIVERS = 'filter[driver_ids]',
  DROPOFF_SITE = 'filter[dropoff_site_ids]',
  PICKUP_SITE = 'filter[pickup_site_ids]',
  RATE_TYPES = 'filter[rate_types]',
  SEARCH_QUERY = 'search[datagrid]',
}

export interface SettlementSearchParams {
  [SettlementSearchParamName.SETTLEMENT_CATEGORY]?: InvoiceCategoryFilter;
  [SettlementSearchParamName.END_DATE]?: string;
  [SettlementSearchParamName.START_DATE]?: string;
  [SettlementSearchParamName.PROJECTS]?: string[];
  [SettlementSearchParamName.CUSTOMERS]?: string[];
  [SettlementSearchParamName.VENDORS]?: string[];
  [SettlementSearchParamName.TRUCKS]?: string[];
  [SettlementSearchParamName.DRIVERS]?: string[];
  [SettlementSearchParamName.DROPOFF_SITE]?: string[];
  [SettlementSearchParamName.PICKUP_SITE]?: string[];
  [SettlementSearchParamName.SETTLEMENT_STATES]?: Array<SettlementState>;
  [SettlementSearchParamName.RATE_TYPES]?: RateType[];
  [SettlementSearchParamName.SEARCH_QUERY]?: string;
}

// The settlement states behind each status chip (SettlementStatusFilters).
export enum SettlementStatusFilter {
  PENDING = 'pending',
  APPROVED = 'approved',
  DONE = 'done',
  VOID = 'void',
}

export const settlementStatesByFilter: Record<SettlementStatusFilter, SettlementState[]> =
  {
    [SettlementStatusFilter.PENDING]: [
      SettlementState.PENDING,
      SettlementState.CUSTOMER_PENDING,
    ],
    [SettlementStatusFilter.APPROVED]: [
      SettlementState.APPROVED,
      SettlementState.CUSTOMER_APPROVED,
    ],
    [SettlementStatusFilter.DONE]: [SettlementState.PAID, SettlementState.CUSTOMER_PAID],
    [SettlementStatusFilter.VOID]: [SettlementState.VOIDED],
  };

// Fresh-load default: every settlement state except the Void chip's — voided
// settlements only appear when explicitly selected. Also the post-clear states
// applied by clearFiltersExceptSearch and assumed by the unfiltered-results
// probe (TRE-15529).
export function getDefaultSettlementStates() {
  return Object.entries(settlementStatesByFilter)
    .filter(([filterKey]) => filterKey !== SettlementStatusFilter.VOID)
    .flatMap(([, states]) => states);
}

const isSetSearchParam = (value: unknown) =>
  value !== undefined && value !== '' && !(Array.isArray(value) && value.length === 0);

export default class SettlementsStore {
  addOnChargesBySettlementId: Record<string, AddOnCharge[]> = {};
  autoExtendedFromRange: { term: string; startDate?: string; endDate?: string } | null =
    null;
  datesDroppedByAutoExtend = false;
  hasUnfilteredResults = false;
  searchParams: SettlementSearchParams = {};
  sortOption: OrderOption<SettlementSort> = {
    orderBy: SettlementSort.CREATED_AT,
    sortBy: 'desc',
  };
  userChosenDateScopeTerm: string | null = null;

  constructor() {
    makeObservable(this, {
      addOnChargesBySettlementId: observable,
      autoExtendedFromRange: observable,
      datesDroppedByAutoExtend: observable,
      hasUnfilteredResults: observable,
      searchParams: observable,
      sortOption: observable,
      userChosenDateScopeTerm: observable,
      clearingFiltersOnlyDropsDates: computed,
      hasActiveNonSearchParams: computed,
      isDateScopeAutoExtended: computed,
      trimmedSearchTerm: computed,
    });
  }

  // Category and states are identity (the tab and its default chip state), and the
  // search term never counts — everything else set means the user narrowed the list.
  get hasActiveNonSearchParams() {
    const {
      [SettlementSearchParamName.SETTLEMENT_CATEGORY]: _category,
      [SettlementSearchParamName.SETTLEMENT_STATES]: _states,
      [SettlementSearchParamName.SEARCH_QUERY]: _searchQuery,
      ...rest
    } = this.searchParams;

    return Object.values(rest).some(isSetSearchParam);
  }

  get clearingFiltersOnlyDropsDates() {
    const {
      [SettlementSearchParamName.SETTLEMENT_CATEGORY]: _category,
      [SettlementSearchParamName.SETTLEMENT_STATES]: states = [],
      [SettlementSearchParamName.SEARCH_QUERY]: _searchQuery,
      [SettlementSearchParamName.START_DATE]: _startDate,
      [SettlementSearchParamName.END_DATE]: _endDate,
      ...rest
    } = this.searchParams;

    return (
      xor(states, getDefaultSettlementStates()).length === 0 &&
      !Object.values(rest).some(isSetSearchParam)
    );
  }

  get isDateScopeAutoExtended() {
    const extendedTerm = this.autoExtendedFromRange?.term;

    return (
      Boolean(extendedTerm) &&
      extendedTerm === this.trimmedSearchTerm &&
      extendedTerm !== this.userChosenDateScopeTerm &&
      !this.searchParams[SettlementSearchParamName.START_DATE] &&
      !this.searchParams[SettlementSearchParamName.END_DATE]
    );
  }

  get trimmedSearchTerm() {
    return this.searchParams[SettlementSearchParamName.SEARCH_QUERY]?.trim() ?? '';
  }

  setAddOnChargesBySettlementId(settlementId: string, addOnCharges: AddOnCharge[]) {
    runInAction(() => {
      this.addOnChargesBySettlementId = {
        ...this.addOnChargesBySettlementId,
        [settlementId]: addOnCharges,
      };
    });
  }

  updateAddOnChargeBySettlementId(settlementId: string, addOnCharge: AddOnCharge) {
    runInAction(() => {
      const updateInd = this.addOnChargesBySettlementId[settlementId].findIndex(
        (charge) => charge.id === addOnCharge.id,
      );
      if (updateInd >= 0) {
        this.addOnChargesBySettlementId[settlementId][updateInd] = addOnCharge;
      }
      this.addOnChargesBySettlementId[settlementId].push(addOnCharge);
    });
  }

  setHasUnfilteredResults(hasUnfilteredResults: boolean) {
    runInAction(() => {
      this.hasUnfilteredResults = hasUnfilteredResults;
    });
  }

  clearFiltersExceptSearch() {
    runInAction(() => {
      // One atomic write: keep the category tab + search term and land the states
      // on the fresh-load default, dropping every other filter. The store ends on
      // exactly the query the unfiltered-results probe validated, and the
      // follow-up URL cleanup re-derives a value-equal params object — a single
      // refetch (TRE-15529).
      this.searchParams = {
        [SettlementSearchParamName.SETTLEMENT_CATEGORY]:
          this.searchParams[SettlementSearchParamName.SETTLEMENT_CATEGORY],
        [SettlementSearchParamName.SETTLEMENT_STATES]: getDefaultSettlementStates(),
        [SettlementSearchParamName.SEARCH_QUERY]:
          this.searchParams[SettlementSearchParamName.SEARCH_QUERY],
      };
    });
  }

  extendSearchToAllDates() {
    runInAction(() => {
      const {
        [SettlementSearchParamName.START_DATE]: startDate,
        [SettlementSearchParamName.END_DATE]: endDate,
        ...searchParamsWithoutDates
      } = this.searchParams;

      this.autoExtendedFromRange = {
        endDate,
        startDate,
        term: this.trimmedSearchTerm,
      };
      this.datesDroppedByAutoExtend = true;
      this.searchParams = searchParamsWithoutDates;
    });
  }

  restoreAutoExtendedRange() {
    runInAction(() => {
      if (!this.autoExtendedFromRange) {
        return;
      }

      const { endDate, startDate, term } = this.autoExtendedFromRange;

      this.searchParams = {
        ...this.searchParams,
        [SettlementSearchParamName.START_DATE]: startDate,
        [SettlementSearchParamName.END_DATE]: endDate,
      };
      this.userChosenDateScopeTerm = term;
      this.autoExtendedFromRange = null;
      this.datesDroppedByAutoExtend = false;
    });
  }

  markDateScopeUserChosen() {
    runInAction(() => {
      this.userChosenDateScopeTerm = this.trimmedSearchTerm;
      this.datesDroppedByAutoExtend = false;
    });
  }

  resetDateScope() {
    runInAction(() => {
      this.autoExtendedFromRange = null;
      this.datesDroppedByAutoExtend = false;
      this.userChosenDateScopeTerm = null;
    });
  }

  setSearchParams(searchParams: SettlementSearchParams) {
    runInAction(() => {
      const searchTermChanged =
        (searchParams[SettlementSearchParamName.SEARCH_QUERY]?.trim() ?? '') !==
        this.trimmedSearchTerm;

      if (searchTermChanged) {
        // Unlike Dispatch, a new term forgets the dropped range: returning to an old
        // term must not revive a banner that restores another search's dates.
        this.autoExtendedFromRange = null;
        this.userChosenDateScopeTerm = null;
      }

      this.searchParams = searchParams;
    });
  }

  setSort(sortOption: OrderOption<SettlementSort>) {
    runInAction(() => {
      this.sortOption = sortOption;
    });
  }
}
